/**
 * Computed-tag expression engine — thin wrapper around math.js.
 *
 * math.js is the IIoT-grade choice: safe parse-then-evaluate (no JS
 * `eval`), wide function library (arithmetic, trig, log/exp, statistical,
 * conditional, comparison), and supports custom function registration —
 * which we use in `stateful.ts` to add window-based operators (ema,
 * rolling_mean, lag, stddev_n).
 *
 * Phase 0 runs the engine client-side so the editor's live preview and
 * the demo work without backend round-trips. Phase 1 ships the same
 * library inside Arun's ingester / window-buffer worker — the spec
 * (`ComputedTagDef`) round-trips unchanged.
 */

import {
  create,
  all,
  type MathNode,
  type ConstantNode,
  type SymbolNode,
} from "mathjs";
import type { ComputedTagDef, ValidationResult } from "./types";
import {
  clearEvalContext,
  registerStatefulFunctions,
  setEvalContext,
} from "./stateful";

/**
 * Use the all-bundle so we get parse(), evaluate(), the full function
 * library and the unit system. We could shave bundle size by importing
 * just the `number` core, but the all-bundle is what makes the engine
 * future-proof for unit-aware math + matrix ops customers may ask for.
 */
const math = create(all, {
  // Keep evaluation in plain numbers (not BigNumbers) for ingester speed.
  number: "number",
});

// Wire the stateful library (ema / rolling_mean / lag / stddev_n) onto
// the same instance. The registration is idempotent — safe under HMR.
registerStatefulFunctions(math);

/** A pre-parsed expression. Cache to avoid re-parsing on every tick. */
export interface CompiledFormula {
  /** Pre-compiled math.js evaluator. */
  evaluate: (scope: Record<string, number>) => number;
  /** All symbol references in the expression (input variables + funcs). */
  symbols: string[];
  /** AST root — useful for tooling / introspection. */
  ast: MathNode;
}

/** All function names math.js exposes. Used to distinguish vars from funcs. */
const FUNCTION_NAMES = new Set<string>(
  Object.entries(math)
    .filter(([_, v]) => typeof v === "function")
    .map(([k]) => k),
);

/**
 * Parse and prepare an expression for repeated evaluation. Throws on
 * parse error.
 *
 * `ownerId` (default `"_anon"`) scopes the stateful-function buffers
 * (ema, rolling_mean, lag, stddev_n) — pass the computed-tag id so each
 * tag gets its own buffer set. The editor's live preview uses
 * `"preview"`; the card output uses the tag's id.
 */
export function compile(expression: string, ownerId = "_anon"): CompiledFormula {
  // What surrounds the expression is no part of it: math.js parses a trailing newline as a block of two expressions,
  // whose result is no number, so "A * 2" typed with Enter previewed NaN. Trimmed as the platform's Compile trims it
  // (platform-core/internal/iot/computedtags/eval/engine.go, #1688), so the preview shows what the worker computes.
  const ast = math.parse(expression.trim());
  const compiled = ast.compile();
  const symbols = collectVariableSymbols(ast);
  return {
    evaluate: (scope) => {
      setEvalContext(ownerId);
      try {
        return Number(compiled.evaluate(scope));
      } finally {
        clearEvalContext();
      }
    },
    symbols,
    ast,
  };
}

/**
 * Evaluate once. Convenience wrapper for callers that don't need to
 * cache the compiled form.
 */
export function evaluate(
  expression: string,
  scope: Record<string, number>,
): number {
  return compile(expression).evaluate(scope);
}

/**
 * Validate an expression for a computed-tag spec before save.
 *
 *   - Parse errors → `parseError`
 *   - Symbols in the expression not in the declared input aliases →
 *     `undeclaredSymbols`
 *   - Declared aliases never referenced in the expression →
 *     `unusedAliases`
 *   - Cycle (the expression references the tag's own id, direct or
 *     transitive via input chain) → `cycle: true`
 */
export function validate(
  expression: string,
  declaredAliases: string[],
  options: { selfTagId?: string; resolveTagAlias?: (alias: string) => string | undefined } = {},
): ValidationResult {
  if (!expression.trim()) {
    return { ok: false, parseError: "Expression is empty" };
  }

  let compiled: CompiledFormula;
  try {
    compiled = compile(expression);
  } catch (err) {
    return {
      ok: false,
      parseError: err instanceof Error ? err.message : String(err),
    };
  }

  const usedSymbols = new Set(compiled.symbols);
  const declared = new Set(declaredAliases);

  const undeclared = [...usedSymbols].filter((s) => !declared.has(s));
  const unused = [...declared].filter((s) => !usedSymbols.has(s));

  // Cycle check (direct): if any used symbol resolves back to the
  // tag's own id, flag.
  let cycle = false;
  if (options.selfTagId && options.resolveTagAlias) {
    for (const sym of usedSymbols) {
      const tagId = options.resolveTagAlias(sym);
      if (tagId && tagId === options.selfTagId) {
        cycle = true;
        break;
      }
    }
  }

  return {
    ok: undeclared.length === 0 && !cycle,
    undeclaredSymbols: undeclared.length > 0 ? undeclared : undefined,
    unusedAliases: unused.length > 0 ? unused : undefined,
    cycle: cycle || undefined,
  };
}

/**
 * Walk the AST, collecting symbol names that aren't function calls.
 * These are the variables the expression references.
 */
function collectVariableSymbols(ast: MathNode): string[] {
  const out = new Set<string>();
  ast.traverse((node, _path, _parent) => {
    if (node.type === "SymbolNode") {
      const name = (node as SymbolNode).name;
      // Skip math constants (pi, e, …) and registered functions.
      if (FUNCTION_NAMES.has(name)) return;
      if (name === "pi" || name === "e" || name === "tau" || name === "Infinity" || name === "NaN") return;
      out.add(name);
    }
  });
  return [...out];
}

/**
 * Build the scope object the compiled formula expects, given an input
 * snapshot keyed by tag id. Maps each input's alias → numeric value.
 * Missing values fall through as `NaN` — math.js propagates that,
 * which is the right semantics for "input not yet seen".
 */
export function buildScope(
  def: ComputedTagDef,
  values: Record<string, number | undefined>,
): Record<string, number> {
  const scope: Record<string, number> = {};
  for (const input of def.inputs) {
    const v = values[input.tagId];
    scope[input.alias] = typeof v === "number" ? v : NaN;
  }
  return scope;
}

/**
 * Expose a typed alias for the math object for cases that need to
 * register custom functions (see `stateful.ts`).
 */
export const mathjs = math;

// Re-export node types for callers that want to introspect ASTs.
export type { ConstantNode, MathNode, SymbolNode };
